import { BiBookOpen, BiLock } from 'react-icons/bi';
import { useTutorial } from '../Tutorial/TutorialContext';
import { comoTerminou } from '../Tutorial/progresso';
import { disponivelPara, passosPara, TRILHA, TUTORIAIS } from '../Tutorial/tutoriais';

const plural = (n, singular, varios) => `${n} ${n === 1 ? singular : varios}`;

const SELO = {
  feito: {
    texto: 'Concluído',
    classe: 'bg-vox-green-100 text-vox-green-800 dark:bg-vox-green-900/40 dark:text-vox-green-300',
  },
  pulado: {
    texto: 'Pulado',
    classe: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  },
  proximo: {
    texto: 'Próximo',
    classe: 'bg-vox-blue-100 text-vox-blue-800 dark:bg-vox-blue-900/40 dark:text-vox-blue-200',
  },
};

const CartaoDoTutorial = ({ tutorial, totalDePassos, situacao, aoComecar }) => {
  const Icone = tutorial.icone;
  const selo = SELO[situacao];
  const jaPassou = situacao === 'feito' || situacao === 'pulado';
  const trancado = situacao === 'trancado';

  return (
    <div
      className={`flex flex-col rounded-xl border bg-white p-5 shadow-vox transition-shadow dark:bg-gray-800 ${
        situacao === 'proximo'
          ? 'border-vox-green-500 ring-1 ring-vox-green-500'
          : 'border-gray-200 dark:border-gray-700'
      } ${trancado ? 'opacity-70' : 'hover:shadow-vox-xl'}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-vox-green-50 text-vox-green-700 dark:bg-vox-green-900/30 dark:text-vox-green-400">
          <Icone className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-gray-900 dark:text-white">{tutorial.titulo}</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{tutorial.descricao}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-1 items-end justify-between gap-3">
        {trancado ? (
          <p className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
            <BiLock className="h-4 w-4 shrink-0" />
            Precisa do nível Admin. Fale com o gestor.
          </p>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {plural(totalDePassos, 'passo', 'passos')}
              </span>
              {selo && (
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${selo.classe}`}>
                  {selo.texto}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={aoComecar}
              className={
                jaPassou
                  ? 'rounded-lg border border-vox-green-600 px-4 py-2 text-sm font-semibold text-vox-green-700 transition-colors hover:bg-vox-green-50 dark:text-vox-green-400 dark:hover:bg-vox-green-900/20'
                  : 'rounded-lg bg-vox-green-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-vox-green-700'
              }
            >
              {jaPassou ? 'Refazer' : 'Começar'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const Tutoriais = () => {
  const { iniciar, progresso, isAdmin } = useTutorial();
  const concluido = (id) => progresso.concluidos.includes(id);
  // O progresso conta só o que o nível alcança: o tutorial trancado não impede chegar a 100%.
  const disponiveis = TUTORIAIS.filter((tutorial) => disponivelPara(tutorial, isAdmin));
  const feitos = disponiveis.filter((tutorial) => concluido(tutorial.id)).length;
  const proximoId = disponiveis.find((tutorial) => !concluido(tutorial.id))?.id;
  const percentual = disponiveis.length ? Math.round((feitos / disponiveis.length) * 100) : 0;

  const situacaoDe = (tutorial) => {
    if (!disponivelPara(tutorial, isAdmin)) return 'trancado';
    if (concluido(tutorial.id)) return comoTerminou(progresso, tutorial.id) ?? 'feito';
    return tutorial.id === proximoId ? 'proximo' : null;
  };

  return (
    <div className="w-full space-y-8">
      <div className="rounded-xl border border-vox-blue-200 bg-gradient-to-r from-vox-blue-50 to-vox-green-50 p-6 dark:border-vox-blue-800 dark:from-vox-blue-900/20 dark:to-vox-green-900/20">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-vox-green-600 text-white">
              <BiBookOpen className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-vox-blue-900 dark:text-white">Tutoriais</h1>
              <p className="text-vox-blue-700 dark:text-gray-300">
                Cada tutorial mostra na tela onde clicar, passo a passo. Nos botões que gravam, como Salvar e
                Confirmar, ele só mostra onde ficam.
              </p>
            </div>
          </div>
          <div className="w-full md:w-64">
            <div className="mb-1 flex justify-between text-sm text-vox-blue-900 dark:text-gray-200">
              <span className="font-semibold">
                {feitos} de {disponiveis.length}
              </span>
              <span>{percentual}%</span>
            </div>
            <div
              className="h-2 overflow-hidden rounded-full bg-white/70 dark:bg-gray-700"
              role="progressbar"
              aria-valuenow={feitos}
              aria-valuemin={0}
              aria-valuemax={disponiveis.length}
              aria-label="Tutoriais concluídos"
            >
              <div className="h-full rounded-full bg-vox-green-600" style={{ width: `${percentual}%` }} />
            </div>
          </div>
        </div>
      </div>

      {TRILHA.map((etapa) => {
        const daEtapa = etapa.tutoriais.filter((tutorial) => disponivelPara(tutorial, isAdmin));
        const feitosNaEtapa = daEtapa.filter((tutorial) => concluido(tutorial.id)).length;
        return (
          <section key={etapa.id} aria-labelledby={`etapa-${etapa.id}`}>
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <h2 id={`etapa-${etapa.id}`} className="text-lg font-semibold text-gray-900 dark:text-white">
                {etapa.nome}
              </h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {feitosNaEtapa} de {daEtapa.length}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {etapa.tutoriais.map((tutorial) => (
                <CartaoDoTutorial
                  key={tutorial.id}
                  tutorial={tutorial}
                  totalDePassos={passosPara(tutorial, isAdmin).length}
                  situacao={situacaoDe(tutorial)}
                  aoComecar={() => iniciar(tutorial.id)}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default Tutoriais;
